/**
 * MODULE: domain/sales/frontend/components/accounts/InlineCardEditor.tsx
 * PURPOSE: Inline editor for org chart cards with Business Unit vs Contact toggle
 * DEPENDENCIES: flowbite-react, react-icons
 * PUBLIC API: InlineCardEditor (default)
 * SIDE EFFECTS: None
 * AGENT SAFETY: 🟢 SAFE
 * FILE SIZE: ~213 / 250 lines
 * RECENT: 2026-02-15 - Theme (light/dark); keyboard shortcuts (Esc, Cmd+Enter, Cmd+Backspace)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Button, Label, TextInput, ToggleSwitch } from 'flowbite-react';
import { HiCheck, HiX, HiTrash } from 'react-icons/hi';
import { CardType, type OrgChartNodeData } from './OrgChartNode';

export interface InlineCardEditorProps {
  node: OrgChartNodeData;
  onSave: (updated: Partial<OrgChartNodeData>) => void;
  onCancel: () => void;
  onDelete: () => void;
}

export function InlineCardEditor({
  node,
  onSave,
  onCancel,
  onDelete,
}: InlineCardEditorProps) {
  const [isBusinessUnit, setIsBusinessUnit] = useState(node.cardType === CardType.GROUP);
  const [firstName, setFirstName] = useState(node.first_name ?? '');
  const [lastName, setLastName] = useState(node.last_name ?? '');
  const [title, setTitle] = useState(node.job_title ?? '');
  const [groupType, setGroupType] = useState(node.groupType ?? 'department');

  const firstInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        formRef.current?.requestSubmit();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Backspace') {
        e.preventDefault();
        if (window.confirm('Delete this card?')) onDelete();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel, onDelete]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isBusinessUnit) {
      onSave({
        first_name: firstName.trim(),
        last_name: '',
        job_title: '',
        cardType: CardType.GROUP,
        groupType: groupType as OrgChartNodeData['groupType'],
        isPlaceholder: false,
      });
    } else {
      onSave({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        job_title: title.trim(),
        cardType: CardType.CONTACT,
        isPlaceholder: true,
      });
    }
  };

  const childCount = node.children?.length ?? 0;
  const canDelete = childCount === 0;

  return (
    <div className="relative">
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className={`rounded-lg border-2 p-4 shadow-2xl ${
          isBusinessUnit
            ? 'border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30'
            : 'border-blue-500 dark:border-blue-400 bg-white dark:bg-gray-800'
        }`}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Toggle: Business Unit vs Contact */}
        <div className="mb-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-600 pb-3">
          <Label
            htmlFor="toggle-type"
            className={`text-xs font-medium ${
              isBusinessUnit ? 'text-purple-700 dark:text-purple-300' : 'text-blue-700 dark:text-blue-300'
            }`}
          >
            {isBusinessUnit ? 'Business Unit' : 'Contact'}
          </Label>
          <ToggleSwitch
            id="toggle-type"
            checked={isBusinessUnit}
            onChange={setIsBusinessUnit}
            color="purple"
          />
        </div>

        {isBusinessUnit ? (
          <>
            <div className="mb-3">
              <TextInput
                ref={firstInputRef}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Department/Unit Name"
                sizing="sm"
                className="border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              />
            </div>
            <div className="mb-3">
              <select
                value={groupType}
                onChange={(e) =>
                  setGroupType(e.target.value as 'team' | 'company' | 'department' | 'division')
                }
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-xs text-gray-900 dark:text-white"
              >
                <option value="department">Department</option>
                <option value="team">Team</option>
                <option value="division">Division</option>
                <option value="company">Company</option>
              </select>
            </div>
          </>
        ) : (
          <>
            <div className="mb-2">
              <TextInput
                ref={firstInputRef}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First Name"
                sizing="sm"
                className="border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              />
            </div>
            <div className="mb-2">
              <TextInput
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last Name"
                sizing="sm"
                className="border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div className="mb-3">
              <TextInput
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Job Title"
                sizing="sm"
                className="border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button type="submit" size="xs" color="blue" className="flex-1">
            <HiCheck className="mr-1 h-4 w-4" />
            Save
          </Button>
          <Button type="button" size="xs" color="gray" onClick={onCancel}>
            <HiX className="h-4 w-4" />
          </Button>
          {canDelete && (
            <Button
              type="button"
              size="xs"
              color="red"
              onClick={() => {
                if (window.confirm('Delete this card?')) onDelete();
              }}
            >
              <HiTrash className="h-4 w-4" />
            </Button>
          )}
        </div>
        {/* Keyboard shortcuts hint */}
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
          <p className="text-[10px] text-gray-500 dark:text-gray-400">
            <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300">⏎</kbd> Save •
            <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 ml-2">Esc</kbd> Cancel
          </p>
        </div>
      </form>
    </div>
  );
}

export default InlineCardEditor;
